/**
 * Instagram Comments (Activity › Instagram Comments)
 *
 * Two tabs:
 *   • Comments — read-only inbox of incoming Instagram comments (dark-launch:
 *     empty until commentsEnabled + Meta App Review, so it says so plainly
 *     rather than showing a bare empty table).
 *   • Automation — comment-to-DM rules (CRUD). Editing needs 'full' Instagram
 *     access; a read-only viewer sees the rules but no edit controls.
 *
 * The whole page is reachable only when the Instagram module is active AND the
 * account is connected (AdminLayout hides the nav item otherwise, and
 * ModuleAccessGuard gates the route) — but it also degrades gracefully to a
 * "connect first" state if opened directly while disconnected.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Button,
  Chip,
  IconButton,
  Switch,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  TextField,
  MenuItem,
  FormControlLabel,
  RadioGroup,
  Radio,
  Tooltip,
  Paper,
} from '@mui/material';
import {
  Add,
  EditOutlined,
  DeleteOutline,
  ChatBubbleOutline,
  BoltOutlined,
  ReplyOutlined,
  VisibilityOffOutlined,
  VisibilityOutlined,
  AlternateEmailOutlined,
  OpenInNewRounded,
} from '@mui/icons-material';
import adminApi, {
  InstagramCommentRow,
  InstagramCommentRule,
  InstagramCommentRuleInput,
  InstagramMentionRow,
} from '@/services/admin.api';
import { usePermissionAccess } from '@/hooks/usePermissionAccess';
import InstagramLogo from '@/components/common/InstagramLogo';

const INK = '#0f172a';
const MUTED = '#64748b';
const FAINT = '#94a3b8';
const BORDER = '#e7e9ee';
const ACCENT = '#4f46e5';

function timeAgo(iso?: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  if (s < 604800) return `${Math.floor(s / 86400)}d ago`;
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

/* ─────────────────────────── empty-state shell ───────────────────────────── */

function CenteredState({ icon, title, body, action }: { icon: React.ReactNode; title: string; body: React.ReactNode; action?: React.ReactNode }) {
  return (
    <Box sx={{ textAlign: 'center', py: 8, px: 2, maxWidth: 460, mx: 'auto' }}>
      <Box sx={{ color: FAINT, mb: 1.5, '& svg': { fontSize: 44 } }}>{icon}</Box>
      <Typography sx={{ fontWeight: 700, color: INK, fontSize: '1.05rem' }}>{title}</Typography>
      <Typography variant="body2" sx={{ color: MUTED, mt: 0.75, lineHeight: 1.6 }}>
        {body}
      </Typography>
      {action && <Box sx={{ mt: 2.5 }}>{action}</Box>}
    </Box>
  );
}

/* ─────────────────────────── comments tab ────────────────────────────────── */

function CommentStatusChips({ c }: { c: InstagramCommentRow }) {
  const chips: Array<{ label: string; color: 'default' | 'success' | 'warning' | 'error' | 'info' }> = [];
  if (c.isDeleted) chips.push({ label: 'Deleted', color: 'error' });
  if (c.isHidden) chips.push({ label: 'Hidden', color: 'warning' });
  if (c.publicReplyCommentId) chips.push({ label: 'Replied', color: 'info' });
  if (c.privateReplySentAt) chips.push({ label: 'DM sent', color: 'success' });
  if (chips.length === 0) chips.push({ label: 'New', color: 'default' });
  return (
    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
      {chips.map((ch) => (
        <Chip key={ch.label} size="small" label={ch.label} color={ch.color} variant={ch.color === 'default' ? 'outlined' : 'filled'} />
      ))}
    </Box>
  );
}

function CommentsTab({
  commentsEnabled,
  canModerate,
  canDelete,
}: {
  commentsEnabled: boolean;
  canModerate: boolean;
  canDelete: boolean;
}) {
  const [rows, setRows] = useState<InstagramCommentRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [replyTarget, setReplyTarget] = useState<InstagramCommentRow | null>(null);
  const [replyText, setReplyText] = useState('');
  const [replySaving, setReplySaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<InstagramCommentRow | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    adminApi
      .getInstagramComments({ page: 1, limit: 50 })
      .then((res) => {
        if (!cancelled) setRows(res.comments);
      })
      .catch((err: any) => {
        if (!cancelled) setError(err?.response?.data?.error?.message || err?.message || 'Failed to load comments');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const patchRow = (id: string, patch: Partial<InstagramCommentRow>) =>
    setRows((prev) => prev.map((r) => (r._id === id ? { ...r, ...patch } : r)));

  const handleToggleHidden = async (c: InstagramCommentRow) => {
    const next = !c.isHidden;
    setBusyId(c._id);
    patchRow(c._id, { isHidden: next }); // optimistic
    try {
      await adminApi.setInstagramCommentHidden(c.commentId, next);
    } catch (err: any) {
      patchRow(c._id, { isHidden: c.isHidden }); // revert
      setError(err?.response?.data?.error?.message || err?.message || 'Failed to update comment');
    } finally {
      setBusyId(null);
    }
  };

  const submitReply = async () => {
    if (!replyTarget || !replyText.trim()) return;
    setReplySaving(true);
    try {
      const res = await adminApi.replyToInstagramComment(replyTarget.commentId, replyText.trim());
      patchRow(replyTarget._id, { publicReplyCommentId: res.replyCommentId || 'replied' });
      setReplyTarget(null);
      setReplyText('');
    } catch (err: any) {
      setError(err?.response?.data?.error?.message || err?.message || 'Failed to post reply');
    } finally {
      setReplySaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    const target = deleteTarget;
    setDeleteTarget(null);
    setBusyId(target._id);
    try {
      await adminApi.deleteInstagramComment(target.commentId);
      patchRow(target._id, { isDeleted: true });
    } catch (err: any) {
      setError(err?.response?.data?.error?.message || err?.message || 'Failed to delete comment');
    } finally {
      setBusyId(null);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  const showActions = canModerate || canDelete;

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {!commentsEnabled && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Comment capture isn't active for your workspace yet — it's pending activation (Instagram requires Meta App
          Review before comments can be received). You can set up automation rules now; incoming comments will appear
          here once it's live.
        </Alert>
      )}

      {rows.length === 0 ? (
        <CenteredState
          icon={<ChatBubbleOutline />}
          title="No comments yet"
          body="Comments people leave on your Instagram posts and reels will show up here, newest first."
        />
      ) : (
        <TableContainer component={Paper} variant="outlined" sx={{ borderColor: BORDER, borderRadius: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ '& th': { color: MUTED, fontWeight: 600, borderColor: BORDER } }}>
                <TableCell>From</TableCell>
                <TableCell>Comment</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">When</TableCell>
                {showActions && <TableCell align="right">Actions</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((c) => {
                const acted = c.isDeleted;
                return (
                  <TableRow key={c._id} hover sx={{ '& td': { borderColor: BORDER }, opacity: acted ? 0.6 : 1 }}>
                    <TableCell sx={{ whiteSpace: 'nowrap', fontWeight: 600, color: INK }}>
                      {c.from?.username ? `@${c.from.username}` : c.from?.id || '—'}
                    </TableCell>
                    <TableCell sx={{ color: INK, maxWidth: 420 }}>
                      <Typography variant="body2" sx={{ color: acted ? FAINT : INK, fontStyle: acted ? 'italic' : 'normal' }}>
                        {c.text || <span style={{ color: FAINT }}>(no text)</span>}
                      </Typography>
                    </TableCell>
                    <TableCell><CommentStatusChips c={c} /></TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap', color: FAINT, fontSize: '0.8rem' }}>
                      {timeAgo(c.createdAt)}
                    </TableCell>
                    {showActions && (
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                        {canModerate && !acted && (
                          <>
                            <Tooltip title="Reply publicly">
                              <span>
                                <IconButton size="small" disabled={busyId === c._id} onClick={() => { setReplyTarget(c); setReplyText(''); }} sx={{ color: MUTED }}>
                                  <ReplyOutlined fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title={c.isHidden ? 'Unhide' : 'Hide'}>
                              <span>
                                <IconButton size="small" disabled={busyId === c._id} onClick={() => handleToggleHidden(c)} sx={{ color: MUTED }}>
                                  {c.isHidden ? <VisibilityOutlined fontSize="small" /> : <VisibilityOffOutlined fontSize="small" />}
                                </IconButton>
                              </span>
                            </Tooltip>
                          </>
                        )}
                        {canDelete && !acted && (
                          <Tooltip title="Delete">
                            <span>
                              <IconButton size="small" disabled={busyId === c._id} onClick={() => setDeleteTarget(c)} sx={{ color: MUTED, '&:hover': { color: '#dc2626' } }}>
                                <DeleteOutline fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Public reply dialog */}
      <Dialog open={Boolean(replyTarget)} onClose={replySaving ? undefined : () => setReplyTarget(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>
          Reply to {replyTarget?.from?.username ? `@${replyTarget.from.username}` : 'comment'}
        </DialogTitle>
        <DialogContent dividers>
          {replyTarget?.text && (
            <Typography variant="body2" sx={{ color: MUTED, mb: 2, p: 1.5, bgcolor: '#f8fafc', borderRadius: 1.5 }}>
              “{replyTarget.text}”
            </Typography>
          )}
          <TextField
            label="Your public reply"
            fullWidth
            multiline
            minRows={2}
            value={replyText}
            onChange={(e) => setReplyText(e.target.value)}
            autoFocus
            helperText="Posted publicly under the comment on Instagram."
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setReplyTarget(null)} disabled={replySaving}>Cancel</Button>
          <Button variant="contained" disableElevation onClick={submitReply} disabled={replySaving || !replyText.trim()} sx={{ bgcolor: ACCENT, '&:hover': { bgcolor: '#4338ca' } }}>
            {replySaving ? <CircularProgress size={18} color="inherit" /> : 'Post reply'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirm */}
      <Dialog open={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Delete this comment?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This permanently deletes the comment on Instagram. This can't be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button color="error" variant="contained" disableElevation onClick={handleDelete}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

/* ─────────────────────────── mentions tab ────────────────────────────────── */

function MentionsTab({ commentsEnabled }: { commentsEnabled: boolean }) {
  const [rows, setRows] = useState<InstagramMentionRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    adminApi
      .getInstagramMentions({ page: 1, limit: 50 })
      .then((res) => {
        if (!cancelled) setRows(res.mentions);
      })
      .catch((err: any) => {
        if (!cancelled) setError(err?.response?.data?.error?.message || err?.message || 'Failed to load mentions');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }
  if (error) return <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>;

  return (
    <Box>
      {!commentsEnabled && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Mention capture isn't active yet — it turns on with comments once your app clears Meta App Review.
        </Alert>
      )}
      {rows.length === 0 ? (
        <CenteredState
          icon={<AlternateEmailOutlined />}
          title="No mentions yet"
          body="When someone @mentions your account in their comment or caption, it'll show up here."
        />
      ) : (
        <TableContainer component={Paper} variant="outlined" sx={{ borderColor: BORDER, borderRadius: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ '& th': { color: MUTED, fontWeight: 600, borderColor: BORDER } }}>
                <TableCell>From</TableCell>
                <TableCell>Mention</TableCell>
                <TableCell>Where</TableCell>
                <TableCell align="right">When</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((m) => (
                <TableRow key={m._id} hover sx={{ '& td': { borderColor: BORDER } }}>
                  <TableCell sx={{ whiteSpace: 'nowrap', fontWeight: 600, color: INK }}>
                    {m.fromUsername ? `@${m.fromUsername}` : '—'}
                  </TableCell>
                  <TableCell sx={{ color: INK, maxWidth: 460 }}>
                    <Typography variant="body2" sx={{ color: m.text ? INK : FAINT }}>
                      {m.text || '(content unavailable)'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip
                        size="small"
                        variant="outlined"
                        label={m.mentionType === 'comment_mention' ? 'In a comment' : 'In a caption'}
                      />
                      {m.permalink && (
                        <Tooltip title="Open on Instagram">
                          <IconButton size="small" component="a" href={m.permalink} target="_blank" rel="noopener" sx={{ color: MUTED }}>
                            <OpenInNewRounded fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap', color: FAINT, fontSize: '0.8rem' }}>
                    {timeAgo(m.capturedAt)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}

/* ─────────────────────────── rule editor dialog ──────────────────────────── */

interface RuleFormState {
  name: string;
  enabled: boolean;
  scopeType: 'all' | 'specific_posts';
  mediaIdsText: string;
  keywordsText: string;
  moderationAction: 'none' | 'hide' | 'delete';
  publicReplyTemplate: string;
  privateReplyTemplate: string;
  priority: number;
}

const emptyForm = (): RuleFormState => ({
  name: '',
  enabled: true,
  scopeType: 'all',
  mediaIdsText: '',
  keywordsText: '',
  moderationAction: 'none',
  publicReplyTemplate: '',
  privateReplyTemplate: '',
  priority: 100,
});

function formFromRule(r: InstagramCommentRule): RuleFormState {
  return {
    name: r.name,
    enabled: r.enabled,
    scopeType: r.scope.type,
    mediaIdsText: r.scope.type === 'specific_posts' ? r.scope.mediaIds.join('\n') : '',
    keywordsText: (r.keywords || []).join(', '),
    moderationAction: r.moderationAction || 'none',
    publicReplyTemplate: r.publicReplyTemplate || '',
    privateReplyTemplate: r.privateReplyTemplate || '',
    priority: r.priority,
  };
}

function parseList(text: string, splitter: RegExp): string[] {
  return text.split(splitter).map((s) => s.trim()).filter(Boolean);
}

function RuleDialog({
  open,
  initial,
  saving,
  onClose,
  onSave,
}: {
  open: boolean;
  initial: RuleFormState;
  saving: boolean;
  onClose: () => void;
  onSave: (input: InstagramCommentRuleInput) => void;
}) {
  const [form, setForm] = useState<RuleFormState>(initial);
  const [localError, setLocalError] = useState('');

  useEffect(() => {
    if (open) {
      setForm(initial);
      setLocalError('');
    }
  }, [open, initial]);

  const set = <K extends keyof RuleFormState>(k: K, v: RuleFormState[K]) => setForm((f) => ({ ...f, [k]: v }));

  const handleSave = () => {
    if (!form.name.trim()) return setLocalError('Give the rule a name.');
    const hasReply = Boolean(form.privateReplyTemplate.trim());
    const hasModeration = form.moderationAction !== 'none';
    if (!hasReply && !hasModeration) {
      return setLocalError('A rule must do something: send a DM, auto-moderate the comment (hide/delete), or both.');
    }
    const mediaIds = parseList(form.mediaIdsText, /[\n,]/);
    if (form.scopeType === 'specific_posts' && mediaIds.length === 0) {
      return setLocalError('Add at least one post/reel ID, or set the rule to apply to all posts.');
    }
    const input: InstagramCommentRuleInput = {
      name: form.name.trim(),
      enabled: form.enabled,
      scope: form.scopeType === 'all' ? { type: 'all' } : { type: 'specific_posts', mediaIds },
      keywords: parseList(form.keywordsText, /,/),
      moderationAction: form.moderationAction,
      publicReplyTemplate: form.publicReplyTemplate.trim() || undefined,
      privateReplyTemplate: form.privateReplyTemplate.trim() || undefined,
      priority: Number.isFinite(form.priority) ? form.priority : 100,
    };
    onSave(input);
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>{initial.name ? 'Edit rule' : 'New automation rule'}</DialogTitle>
      <DialogContent dividers>
        {localError && <Alert severity="error" sx={{ mb: 2 }}>{localError}</Alert>}

        <TextField
          label="Rule name"
          fullWidth
          value={form.name}
          onChange={(e) => set('name', e.target.value)}
          placeholder="e.g. Pricing keyword → send price list"
          sx={{ mb: 2.5 }}
          autoFocus
        />

        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: INK, mb: 0.5 }}>
          Which posts
        </Typography>
        <RadioGroup
          value={form.scopeType}
          onChange={(e) => set('scopeType', e.target.value as 'all' | 'specific_posts')}
          sx={{ mb: 1 }}
        >
          <FormControlLabel value="all" control={<Radio size="small" />} label="All posts and reels" />
          <FormControlLabel value="specific_posts" control={<Radio size="small" />} label="Only specific posts" />
        </RadioGroup>
        {form.scopeType === 'specific_posts' && (
          <TextField
            label="Post / reel IDs"
            fullWidth
            multiline
            minRows={2}
            value={form.mediaIdsText}
            onChange={(e) => set('mediaIdsText', e.target.value)}
            helperText="One media ID per line (or comma-separated)."
            sx={{ mb: 2.5 }}
          />
        )}

        <TextField
          label="Keywords (optional)"
          fullWidth
          value={form.keywordsText}
          onChange={(e) => set('keywordsText', e.target.value)}
          helperText="Comma-separated. The rule fires when a comment contains any of these (case-insensitive). Leave blank to match every comment on the selected posts."
          sx={{ mt: form.scopeType === 'specific_posts' ? 0 : 1, mb: 2.5 }}
        />

        <TextField
          select
          label="Auto-moderation"
          fullWidth
          value={form.moderationAction}
          onChange={(e) => set('moderationAction', e.target.value as RuleFormState['moderationAction'])}
          helperText="Optionally act on the comment itself when this rule matches — e.g. hide or delete spam."
          sx={{ mb: 2.5 }}
        >
          <MenuItem value="none">No moderation</MenuItem>
          <MenuItem value="hide">Hide the comment</MenuItem>
          <MenuItem value="delete">Delete the comment</MenuItem>
        </TextField>

        {form.moderationAction === 'delete' ? (
          <Alert severity="info" sx={{ mb: 2.5 }}>
            Deleted comments can't receive a reply or DM, so the reply fields below are ignored for this rule.
          </Alert>
        ) : (
          <>
            <TextField
              label="Public reply (optional)"
              fullWidth
              multiline
              minRows={2}
              value={form.publicReplyTemplate}
              onChange={(e) => set('publicReplyTemplate', e.target.value)}
              helperText="Posted as a public reply under the comment. Leave blank to only send the DM."
              sx={{ mb: 2.5 }}
            />

            <TextField
              label={form.moderationAction === 'none' ? 'Private reply (DM) — required' : 'Private reply (DM) — optional'}
              fullWidth
              multiline
              minRows={2}
              value={form.privateReplyTemplate}
              onChange={(e) => set('privateReplyTemplate', e.target.value)}
              helperText={
                form.moderationAction === 'none'
                  ? 'Sent as a direct message to the commenter. Meta allows exactly one DM per comment, within 7 days.'
                  : 'Optional when a moderation action is set. Meta allows exactly one DM per comment, within 7 days.'
              }
              sx={{ mb: 2.5 }}
            />
          </>
        )}

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
          <TextField
            label="Priority"
            type="number"
            value={form.priority}
            onChange={(e) => set('priority', Number.parseInt(e.target.value, 10))}
            helperText="Lower fires first"
            sx={{ width: 140 }}
          />
          <FormControlLabel
            control={<Switch checked={form.enabled} onChange={(e) => set('enabled', e.target.checked)} />}
            label="Enabled"
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving}
          disableElevation
          sx={{ bgcolor: ACCENT, '&:hover': { bgcolor: '#4338ca' } }}
        >
          {saving ? <CircularProgress size={18} color="inherit" /> : 'Save rule'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/* ─────────────────────────── automation tab ──────────────────────────────── */

function AutomationTab({ canManage }: { canManage: boolean }) {
  const [rules, setRules] = useState<InstagramCommentRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<InstagramCommentRule | null>(null);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<InstagramCommentRule | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setRules(await adminApi.getInstagramCommentRules());
    } catch (err: any) {
      setError(err?.response?.data?.error?.message || err?.message || 'Failed to load rules');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  const openNew = () => {
    setEditing(null);
    setDialogOpen(true);
  };
  const openEdit = (r: InstagramCommentRule) => {
    setEditing(r);
    setDialogOpen(true);
  };

  const handleSave = async (input: InstagramCommentRuleInput) => {
    setSaving(true);
    setError('');
    try {
      if (editing) await adminApi.updateInstagramCommentRule(editing._id, input);
      else await adminApi.createInstagramCommentRule(input);
      setDialogOpen(false);
      setEditing(null);
      await reload();
    } catch (err: any) {
      setError(err?.response?.data?.error?.message || err?.message || 'Failed to save rule');
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (r: InstagramCommentRule) => {
    // Optimistic; revert on failure.
    setRules((prev) => prev.map((x) => (x._id === r._id ? { ...x, enabled: !x.enabled } : x)));
    try {
      await adminApi.updateInstagramCommentRule(r._id, { enabled: !r.enabled });
    } catch (err: any) {
      setRules((prev) => prev.map((x) => (x._id === r._id ? { ...x, enabled: r.enabled } : x)));
      setError(err?.response?.data?.error?.message || err?.message || 'Failed to update rule');
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await adminApi.deleteInstagramCommentRule(deleteTarget._id);
      setDeleteTarget(null);
      await reload();
    } catch (err: any) {
      setError(err?.response?.data?.error?.message || err?.message || 'Failed to delete rule');
    }
  };

  const initialForm = useMemo(() => (editing ? formFromRule(editing) : emptyForm()), [editing]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="body2" sx={{ color: MUTED, maxWidth: 560 }}>
          When someone comments on your posts, these rules auto-reply publicly and send them a DM. Exactly one rule fires
          per comment — the first match by priority.
        </Typography>
        {canManage && (
          <Button variant="contained" startIcon={<Add />} onClick={openNew} disableElevation sx={{ bgcolor: ACCENT, '&:hover': { bgcolor: '#4338ca' }, flexShrink: 0 }}>
            New rule
          </Button>
        )}
      </Box>

      {rules.length === 0 ? (
        <CenteredState
          icon={<BoltOutlined />}
          title="No automation rules yet"
          body={
            canManage
              ? 'Create a rule to automatically reply to comments and turn commenters into DM conversations.'
              : 'No comment-to-DM rules are set up. Ask an admin with full Instagram access to add one.'
          }
          action={
            canManage ? (
              <Button variant="contained" startIcon={<Add />} onClick={openNew} disableElevation sx={{ bgcolor: ACCENT, '&:hover': { bgcolor: '#4338ca' } }}>
                New rule
              </Button>
            ) : undefined
          }
        />
      ) : (
        <TableContainer component={Paper} variant="outlined" sx={{ borderColor: BORDER, borderRadius: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ '& th': { color: MUTED, fontWeight: 600, borderColor: BORDER } }}>
                <TableCell sx={{ width: 64 }}>On</TableCell>
                <TableCell>Rule</TableCell>
                <TableCell>Scope</TableCell>
                <TableCell>Keywords</TableCell>
                <TableCell align="center">Priority</TableCell>
                {canManage && <TableCell align="right">Actions</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {rules.map((r) => (
                <TableRow key={r._id} hover sx={{ '& td': { borderColor: BORDER }, opacity: r.enabled ? 1 : 0.6 }}>
                  <TableCell>
                    <Switch size="small" checked={r.enabled} disabled={!canManage} onChange={() => handleToggle(r)} />
                  </TableCell>
                  <TableCell>
                    <Typography sx={{ fontWeight: 600, color: INK }}>{r.name}</Typography>
                    <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
                      {r.publicReplyTemplate && <Chip size="small" variant="outlined" label="Public reply" />}
                      {r.privateReplyTemplate && <Chip size="small" color="success" label="DM" />}
                      {r.moderationAction === 'hide' && <Chip size="small" color="warning" label="Hide" />}
                      {r.moderationAction === 'delete' && <Chip size="small" color="error" label="Delete" />}
                    </Box>
                  </TableCell>
                  <TableCell sx={{ color: MUTED, fontSize: '0.82rem' }}>
                    {r.scope.type === 'all' ? 'All posts' : `${r.scope.mediaIds.length} post(s)`}
                  </TableCell>
                  <TableCell sx={{ color: MUTED, fontSize: '0.82rem', maxWidth: 220 }}>
                    {r.keywords && r.keywords.length ? r.keywords.join(', ') : <span style={{ color: FAINT }}>Any comment</span>}
                  </TableCell>
                  <TableCell align="center" sx={{ color: MUTED }}>{r.priority}</TableCell>
                  {canManage && (
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => openEdit(r)} sx={{ color: MUTED }}>
                          <EditOutlined fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton size="small" onClick={() => setDeleteTarget(r)} sx={{ color: MUTED, '&:hover': { color: '#dc2626' } }}>
                          <DeleteOutline fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <RuleDialog
        open={dialogOpen}
        initial={initialForm}
        saving={saving}
        onClose={() => {
          setDialogOpen(false);
          setEditing(null);
        }}
        onSave={handleSave}
      />

      <Dialog open={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Delete “{deleteTarget?.name}”?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This rule will stop replying to comments and sending DMs. This can't be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button color="error" variant="contained" disableElevation onClick={handleDelete}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

/* ─────────────────────────── page ────────────────────────────────────────── */

const InstagramComments: React.FC = () => {
  const { hasPermission } = usePermissionAccess();
  const canManage = hasPermission('instagram', 'full'); // rule CRUD
  const canModerate = hasPermission('instagram', 'edit'); // reply / hide
  const canDelete = hasPermission('instagram', 'full'); // delete a comment

  const [tab, setTab] = useState(0);
  const [connected, setConnected] = useState<boolean | null>(null);
  const [commentsEnabled, setCommentsEnabled] = useState(false);

  useEffect(() => {
    let cancelled = false;
    adminApi
      .getInstagramConfig()
      .then((cfg) => {
        if (cancelled) return;
        setConnected(Boolean(cfg.isConnected));
        setCommentsEnabled(Boolean((cfg as any).commentsEnabled));
      })
      .catch(() => {
        if (!cancelled) setConnected(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <Box sx={{ maxWidth: 1040, mx: 'auto', px: { xs: 2, sm: 0 }, py: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
        <InstagramLogo size={26} />
        <Typography sx={{ fontWeight: 800, color: INK, fontSize: '1.35rem' }}>Instagram Comments</Typography>
      </Box>
      <Typography variant="body2" sx={{ color: MUTED, mb: 3 }}>
        See comments on your posts and turn commenters into conversations automatically.
      </Typography>

      {connected === null ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress size={24} />
        </Box>
      ) : !connected ? (
        <CenteredState
          icon={<InstagramLogo size={40} />}
          title="Connect Instagram first"
          body="Once your Instagram account is connected, comments and automation will appear here."
          action={
            <Button component={RouterLink} to="/admin/settings?tab=instagram" variant="contained" disableElevation sx={{ bgcolor: ACCENT, '&:hover': { bgcolor: '#4338ca' } }}>
              Go to Instagram settings
            </Button>
          }
        />
      ) : (
        <>
          <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: `1px solid ${BORDER}`, mb: 2.5, '& .MuiTab-root': { textTransform: 'none', fontWeight: 600 } }}>
            <Tab icon={<ChatBubbleOutline sx={{ fontSize: 18 }} />} iconPosition="start" label="Comments" />
            <Tab icon={<AlternateEmailOutlined sx={{ fontSize: 18 }} />} iconPosition="start" label="Mentions" />
            <Tab icon={<BoltOutlined sx={{ fontSize: 18 }} />} iconPosition="start" label="Automation" />
          </Tabs>
          {tab === 0 && <CommentsTab commentsEnabled={commentsEnabled} canModerate={canModerate} canDelete={canDelete} />}
          {tab === 1 && <MentionsTab commentsEnabled={commentsEnabled} />}
          {tab === 2 && <AutomationTab canManage={canManage} />}
        </>
      )}
    </Box>
  );
};

export default InstagramComments;
